import { useState, useEffect, useRef } from "react";
import { api } from "../lib/api";
import { Search, Check } from "lucide-react";

export function ProductCombobox({ item, field, onSelect, onInvalidate, testid, dark = false }) {
  const [query, setQuery] = useState(field === "code" ? item.product_code || "" : item.product_name || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);
  const focusedRef = useRef(false);

  // Re-sync displayed text from the item ONLY when the user isn't typing here
  // (prevents clobbering keystrokes; still updates when the sibling field selects a product).
  useEffect(() => {
    if (focusedRef.current) return;
    setQuery(field === "code" ? item.product_code || "" : item.product_name || "");
  }, [item.product_code, item.product_name, item.product_id, field]);

  useEffect(() => {
    if (!open) return;
    const h = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/products", { params: { search: query, active_only: true } });
        setResults(data);
      } catch {
        setResults([]);
      }
      setLoading(false);
    }, 200);
    return () => clearTimeout(h);
  }, [query, open]);

  useEffect(() => {
    const onDoc = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const choose = (p) => {
    setQuery(field === "code" ? p.product_code : p.product_name);
    onSelect(p);
    setOpen(false);
  };

  const inputBase = dark ? "border-slate-700 bg-slate-800 text-white" : "border-border bg-white";
  const ddBase = dark ? "border-slate-700 bg-slate-900 text-slate-100" : "border-border bg-white";

  return (
    <div ref={boxRef} className="relative">
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          data-testid={testid}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            if (onInvalidate) onInvalidate(e.target.value);
          }}
          onFocus={() => { focusedRef.current = true; setOpen(true); }}
          onBlur={() => { focusedRef.current = false; }}
          autoComplete="off"
          placeholder={field === "code" ? "Ürün kodu ara..." : "Ürün adı ara..."}
          className={`w-full rounded-xl border py-2.5 pl-9 pr-8 text-sm outline-none focus:border-primary ${field === "code" ? "font-mono" : ""} ${inputBase}`}
        />
        {item.product_id && <Check className="absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-emerald-500" />}
      </div>
      {open && (
        <div data-testid={`${testid}-dropdown`} className={`absolute z-40 mt-1 max-h-64 w-full overflow-auto rounded-xl border shadow-xl ${ddBase}`}>
          {loading && <div className="px-3 py-2 text-sm text-muted-foreground">Aranıyor...</div>}
          {!loading && results.length === 0 && <div className="px-3 py-3 text-sm text-muted-foreground">Ürün bulunamadı</div>}
          {!loading &&
            results.map((p) => (
              <button
                key={p.id}
                type="button"
                data-testid={`product-option-${p.product_code}`}
                onClick={() => choose(p)}
                className={`flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm transition-colors ${dark ? "hover:bg-slate-800" : "hover:bg-secondary"}`}
              >
                <span className="font-mono font-bold text-primary">{p.product_code}</span>
                <span className="text-muted-foreground">–</span>
                <span className="truncate">{p.product_name}</span>
                {!p.is_active && <span className="ml-auto text-xs text-rose-500">pasif</span>}
              </button>
            ))}
        </div>
      )}
    </div>
  );
}
